/**
 * Fond de carte clair, source unique pour toutes les cartes du back-office.
 *
 * Style CARTO « Voyager » : terres crème, routes discrètes, le plus proche du
 * fond beige de la charte (#ECE8DF). « Positron » (light_all), l'autre fond
 * clair de CARTO, est gris froid et jure avec le beige.
 *
 * CARTO exige une clé depuis août 2026 : sans elle, les tuiles reviennent
 * barrées d'un filigrane « API KEY REQUIRED ». La clé est gratuite,
 * plafonnée à 5 millions de tuiles par mois civil.
 *
 * Elle est lue dans REACT_APP_CARTO_KEY et jamais écrite en dur. Une variable
 * REACT_APP_* est résolue à la COMPILATION, pas à l'exécution : elle doit
 * donc être présente au moment du `npm run build`, y compris à l'intérieur de
 * l'image Docker : deploy-fast.sh la passe en --build-arg, le Dockerfile
 * la déclare en ARG puis ENV avant `npm run build`.
 *
 * La clé se retrouve nécessairement dans le bundle servi au navigateur —
 * c'est inhérent à un fond de carte côté client, et CARTO en tient compte
 * dans son modèle. La passer par l'environnement sert à pouvoir la changer
 * sans toucher au code, et à ne pas l'inscrire dans l'historique git.
 */

const CLE_CARTO = (process.env.REACT_APP_CARTO_KEY ?? "").trim();

/** CARTO publie quatre sous-domaines, pas trois. */
export const SOUS_DOMAINES_CARTO = ["a", "b", "c", "d"] as const;

/** Chemin du style CARTO retenu (fond clair). */
const STYLE_CARTO = "rastertiles/voyager";

/** Vrai si le fond CARTO est utilisable (clé présente au build). */
export const fondCartoDisponible = CLE_CARTO !== "";

/* Repli quand la clé est absente : OpenStreetMap standard. Clair lui aussi,
   mais plus chargé ; une carte lisible en repli vaut mieux qu'une carte
   couverte de filigranes « API KEY REQUIRED ». */
const OSM_MODELE = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_SOUS_DOMAINES = ["a", "b", "c"] as const;

if (!fondCartoDisponible) {
  // eslint-disable-next-line no-console
  console.warn(
    "[basemap] REACT_APP_CARTO_KEY absente au build : repli sur OpenStreetMap. " +
    "Le fond de carte sera celui d'OpenStreetMap. La clé est passée par deploy-fast.sh (--build-arg) au Dockerfile."
  );
}

/** Modèle d'URL pour Leaflet, qui substitue lui-même {s}. */
export const modeleTuilesLeaflet = (retina = false): string =>
  fondCartoDisponible
    ? `https://{s}.basemaps.cartocdn.com/${STYLE_CARTO}/{z}/{x}/{y}${retina ? "{r}" : ""}.png?key=${CLE_CARTO}`
    : OSM_MODELE;

/** Sous-domaines à déclarer à Leaflet, accordés à la source retenue. */
export const sousDomainesLeaflet = (): string =>
  fondCartoDisponible ? SOUS_DOMAINES_CARTO.join("") : OSM_SOUS_DOMAINES.join("");

/** Liste d'URL pour deck.gl, qui ne substitue pas {s} et attend un tableau. */
export const urlsTuilesDeckGl = (): string[] => {
  const domaines = fondCartoDisponible ? SOUS_DOMAINES_CARTO : OSM_SOUS_DOMAINES;
  return domaines.map((d) =>
    fondCartoDisponible
      ? `https://${d}.basemaps.cartocdn.com/${STYLE_CARTO}/{z}/{x}/{y}.png?key=${CLE_CARTO}`
      : OSM_MODELE.replace("{s}", d)
  );
};

/** Attribution à afficher. CARTO impose de créditer CARTO et OpenStreetMap. */
export const attributionFond = fondCartoDisponible
  ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
  : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
